import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Marquee from "@/components/Marquee";
import { FadeIn, ClipReveal, MaskedLine, Eyebrow, EASE } from "@/components/Reveal";
import { useSiteContent } from "@/hooks/useSiteContent";
import { COMPANY, IMAGES, SERVICES, SOA, STATS, INNOVATION, MARQUEE_ITEMS } from "@/data/content";

const ROAD_PATH = "M0 54 C 130 10, 330 4, 520 40";

function Hero({ content }) {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 200]);

  return (
    <section ref={ref} className="relative isolate flex min-h-[100svh] flex-col justify-end overflow-hidden" data-testid="home-hero">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10" aria-hidden="true">
        <img src={content.hero_image} alt="" className="h-[120%] w-full object-cover" />
        <div className="absolute inset-0 bg-brand-teal/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-slate via-brand-slate/20 to-brand-slate/60" />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(120% 90% at 80% 10%, rgba(0,180,216,0.18), transparent 60%)" }}
        />
      </motion.div>
      <div className="grid-lines absolute inset-0 -z-10 opacity-70" aria-hidden="true" />

      <div className="mx-auto w-full max-w-7xl px-5 pb-14 pt-40 sm:px-8 lg:pb-20">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-ice">
            {COMPANY.fullName} — dal {COMPANY.founded}
          </p>
        </motion.div>

        <h1
          className="mt-6 font-display text-[clamp(3.8rem,15vw,10.5rem)] font-bold uppercase leading-[0.88] tracking-tight"
          data-testid="hero-title"
        >
          <MaskedLine delay={0.25}>Tecnologie</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-outline">Edili</span>
          </MaskedLine>
        </h1>

        <svg
          viewBox="0 0 520 60"
          className="mt-4 h-10 w-[min(520px,80%)] md:h-14"
          fill="none"
          aria-hidden="true"
          data-testid="hero-road-svg"
        >
          <defs>
            <linearGradient id="hero-road" x1="0" y1="60" x2="520" y2="0">
              <stop offset="0" stopColor="#E63946" />
              <stop offset="0.5" stopColor="#FF6B00" />
              <stop offset="1" stopColor="#FFB703" />
            </linearGradient>
          </defs>
          <motion.path
            d={ROAD_PATH}
            stroke="url(#hero-road)"
            strokeWidth="6"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.6, delay: 0.9, ease: EASE }}
          />
          <motion.circle
            cx="520"
            cy="40"
            r="5"
            fill="#9AA5AD"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.4, duration: 0.4 }}
          />
        </svg>

        <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
            className="max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subline"
          >
            {content.hero_subline}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.25, ease: EASE }}
            className="flex flex-wrap gap-4"
          >
            <Link
              to="/contatti"
              data-testid="hero-cta-preventivo"
              className="group flex items-center gap-3 bg-brand-cyan px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-brand-teal transition hover:bg-brand-ice"
            >
              Richiedi un preventivo
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/attivita-e-servizi"
              data-testid="hero-cta-servizi"
              className="flex items-center gap-3 border border-white/25 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition hover:border-brand-ice hover:text-brand-ice"
            >
              I nostri servizi
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-14 grid grid-cols-2 gap-px border border-white/10 bg-white/10 lg:grid-cols-4"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-brand-slate/80 px-6 py-5 backdrop-blur-sm">
              <p className="font-display text-3xl font-bold text-white sm:text-4xl" data-testid="hero-stat-value">
                {s.value}
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-ice">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function IntroSplit({ content }) {
  return (
    <section className="py-24 lg:py-32" data-testid="home-intro">
      <div className="mx-auto grid w-full max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <ClipReveal className="relative">
          <div className="relative overflow-hidden">
            <img src={content.intro_image} alt="Cantiere Eredi Cavallo" className="aspect-[4/3] w-full object-cover transition duration-700 hover:scale-105" />
            <div className="absolute inset-0 border border-white/10" />
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
            Le nostre maestranze al lavoro
          </p>
        </ClipReveal>
        <div className="flex flex-col justify-center">
          <Eyebrow>Chi siamo</Eyebrow>
          <FadeIn>
            <h2 className="mt-6 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
              Una tradizione familiare
              <span className="block text-brand-ice">dal 1958</span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-8 text-base leading-relaxed text-slate-400 sm:text-lg">
              {content.intro_text}
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <Link
              to="/chi-siamo"
              data-testid="home-intro-link"
              className="group mt-10 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-brand-ice transition hover:text-white"
            >
              Scopri la nostra storia
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="py-24 lg:py-32" data-testid="home-services">
      <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Eyebrow>Attività e servizi</Eyebrow>
            <FadeIn delay={0.05}>
              <h2 className="mt-6 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
                Specializzazioni
                <span className="block text-outline-thin">di cantiere</span>
              </h2>
            </FadeIn>
          </div>
          <FadeIn delay={0.15}>
            <Link
              to="/attivita-e-servizi"
              data-testid="home-services-all-link"
              className="group flex items-center gap-3 border border-white/20 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white transition hover:border-brand-ice hover:text-brand-ice"
            >
              Tutte le attività
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeIn>
        </div>

        <div className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <FadeIn key={s.id} delay={0.06 * i} amount={0.2}>
              <Link
                to="/attivita-e-servizi"
                data-testid={`services-preview-card-${s.id}`}
                className="group flex h-full flex-col justify-between bg-brand-slate p-8 transition-colors duration-500 hover:bg-brand-card"
              >
                <div>
                  <p className="font-mono text-xs text-brand-cyan">0{i + 1}</p>
                  <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-tight text-white transition-colors group-hover:text-brand-ice sm:text-3xl">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                </div>
                <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                  {s.tag}
                </p>
              </Link>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function InnovationBanner({ content }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <section ref={ref} className="relative isolate flex min-h-[70vh] items-center overflow-hidden" data-testid="home-innovation">
      <motion.div style={{ y }} className="absolute inset-0 -z-10 scale-110" aria-hidden="true">
        <img src={IMAGES.mountainInfra} alt="" className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-brand-teal/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-slate/90 via-transparent to-brand-slate/90" />
      </motion.div>
      <div className="mx-auto w-full max-w-7xl px-5 py-24 sm:px-8">
        <Eyebrow>{INNOVATION.subtitle}</Eyebrow>
        <FadeIn>
          <h2 className="mt-6 max-w-3xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
            {INNOVATION.title}
          </h2>
        </FadeIn>
        <FadeIn delay={0.15}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-200 sm:text-lg">
            {content.innovation_text}
          </p>
        </FadeIn>
      </div>
    </section>
  );
}

function CertStrip() {
  return (
    <section className="py-24 lg:py-32" data-testid="home-certifications">
      <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
          <div>
            <Eyebrow>Qualità certificata</Eyebrow>
            <FadeIn delay={0.05}>
              <h2 className="mt-6 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl">
                Certificata
                <span className="block text-brand-ice">UNI EN ISO 9001</span>
              </h2>
            </FadeIn>
            <FadeIn delay={0.15}>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                Attestazione SOA per le categorie:
              </p>
            </FadeIn>
            <FadeIn delay={0.2}>
              <Link
                to="/mission"
                data-testid="home-cert-link"
                className="group mt-8 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-brand-ice transition hover:text-white"
              >
                Vedi tutte le certificazioni
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </FadeIn>
          </div>
          <div className="flex flex-wrap content-start gap-3">
            {SOA.map((c, i) => (
              <FadeIn key={c.code} delay={0.05 * i} amount={0.3}>
                <div
                  data-testid={`soa-badge-${c.code.replace(/\s/g, "").toLowerCase()}`}
                  className="border border-brand-gold/40 bg-brand-card px-5 py-4 transition hover:border-brand-gold"
                >
                  <p className="soa-gold bg-clip-text font-mono text-sm font-semibold uppercase tracking-[0.15em] text-transparent">
                    {c.code} {c.level}
                  </p>
                  <p className="mt-2 max-w-[220px] text-xs leading-snug text-slate-400">{c.title}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaBanner({ content }) {
  return (
    <section className="relative overflow-hidden border-t border-white/10 bg-brand-teal py-24 lg:py-28" data-testid="home-cta">
      <div className="grid-lines absolute inset-0 opacity-50" aria-hidden="true" />
      <div className="relative mx-auto flex w-full max-w-7xl flex-col items-start gap-10 px-5 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <FadeIn>
            <h2 className="max-w-2xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-6xl">
              Parliamo del tuo progetto
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-300">
              {content.mission_quote}
            </p>
          </FadeIn>
        </div>
        <FadeIn delay={0.2}>
          <Link
            to="/contatti"
            data-testid="home-cta-button"
            className="group flex items-center gap-3 bg-brand-cyan px-9 py-5 font-mono text-xs uppercase tracking-[0.2em] text-brand-teal transition hover:bg-brand-ice"
          >
            Contattaci ora
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeIn>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { content } = useSiteContent();
  return (
    <>
      <Hero content={content} />
      <Marquee items={MARQUEE_ITEMS} />
      <IntroSplit content={content} />
      <ServicesPreview />
      <InnovationBanner content={content} />
      <CertStrip />
      <CtaBanner content={content} />
    </>
  );
}
